import React from "react";
//#region types
import { FileConstants, IconDefinitionConstants } from "utils/constants";
import { FileMetaData, FileViewerDisplayState, FileViewerProps } from "models/propTypes/fileViewerPropTypes";
import { TranslationKeys } from "translations";
import { FileEnums } from "models/enums";
//#endregion
//#region components
import { FileViewerContext } from "components/common/fileViewer/context";
import { Divider, Grid } from "@mui/material";
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef,
  ReactZoomPanPinchHandlers,
} from "react-zoom-pan-pinch";
import FileViewerInner from "./fileViewerInner";
import RoundButton from "../buttons/roundButton";
import InputField from "../forms/baseFields/inputField";
import LoadingWithLogo from "../loading/loadingWithLogo";
import EntityPagination from "../drawers/entityView/entityPagination";
import Icon from "../icon";
import ReactDOM from "react-dom";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { FileHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import { ImageService } from "services";

class FileViewer extends React.Component<FileViewerProps, FileViewerDisplayState> {
  static contextType = FileViewerContext;
  context!: React.ContextType<typeof FileViewerContext>;

  containerRef: HTMLDivElement | undefined = undefined;
  imageActionContainerRef: HTMLDivElement | undefined = undefined;
  frameRef: HTMLImageElement | undefined = undefined;
  frameRefMounted: boolean = false;
  mounted: boolean = true;
  initialIndex: number = 0;

  readonly maxZoom = 360;
  readonly zoomScale = 0.1;

  constructor(props: FileViewerProps) {
    super(props);

    this.state = {
      containerHeight: 0,
      containerWidth: 0,
      zoom: 100,
      scrollPosition: 0,
    };
  }

  componentDidMount() {
    this.mounted = true;
    this.initialIndex = this.props.currentPage - 1;
    window.addEventListener("resize", this.setFrameWidthHeight);
    document.querySelector(`#${FileConstants.FileViewerFullScreenId}`)?.addEventListener("scroll", this.listenToScroll);
  }

  componentWillUnmount() {
    this.mounted = false;
    window.removeEventListener("resize", this.setFrameWidthHeight);
    document
      .querySelector(`#${FileConstants.FileViewerFullScreenId}`)
      ?.removeEventListener("scroll", this.listenToScroll);

    this.containerRef = undefined;
    this.imageActionContainerRef = undefined;
    this.frameRef = undefined;
  }

  setFrameWidthHeight = () => {
    const containerWidth = this.containerRef ? this.containerRef.clientWidth : 0;
    const containerHeight = this.containerRef ? this.containerRef.clientHeight : 0;
    const imageWidth = containerWidth;
    let imageHeight = containerWidth * 1.42;

    if (containerHeight && containerWidth > containerHeight) {
      imageHeight = containerHeight;
    }

    const setFrame = () => {
      this.mounted &&
        this.setState({
          ...this.state,
          containerWidth: imageWidth,
          containerHeight: imageHeight,
        });
    };

    setFrame();
  };

  getImageDimensions = (file: string) => {
    return new Promise<{ w: number; h: number }>(function (resolved, rejected) {
      const i = new Image();
      i.onload = function () {
        resolved({ w: i.width, h: i.height });
      };
      i.src = file;
    });
  };

  onCurrentIndexUpdate = (index: number) => {
    this.setState({ zoom: 100 });
    this.props.loadImage(index);
  };

  onZoomIn = (zoomFunc: (step?: number) => void) => {
    zoomFunc(this.zoomScale);

    this.setState((prevState) => {
      const updatedZoom = prevState.zoom + this.zoomScale * 100;
      return { ...prevState, zoom: updatedZoom > this.maxZoom ? this.maxZoom : updatedZoom };
    });
  };

  onZoomOut = (zoomFunc: (step?: number) => void) => {
    zoomFunc(this.zoomScale);

    this.setState((prevState) => {
      const updatedZoom = this.state.zoom - this.zoomScale * 100;
      return { ...prevState, zoom: updatedZoom < 100 ? 100 : updatedZoom };
    });
  };

  onZoom = (ref: ReactZoomPanPinchRef, event: TouchEvent | MouseEvent) => {
    this.setState((prevState) => {
      // incremental amount => 37.1429 = (this.maxZoom - 100) / (maxScale - minScale)
      const updatedZoom = 100 + (ref.instance.transformState.scale - 1) * 37.1429;
      return { ...prevState, zoom: updatedZoom < 100 ? 100 : updatedZoom > this.maxZoom ? this.maxZoom : updatedZoom };
    });
  };

  onZoomInputChanged = (zoomInFunc: (step?: number) => void, zoomOutFunc: (step?: number) => void, value: string) => {
    if (!UtilsHelper.isEmpty(value)) {
      let inputValue = Number(value);
      inputValue = inputValue < 100 ? 100 : inputValue > this.maxZoom ? this.maxZoom : inputValue;

      const scale = (inputValue - this.state.zoom) / 100;
      if (scale < 0) zoomOutFunc(Math.abs(scale));
      else zoomInFunc(scale);

      this.setState({ zoom: inputValue });
    }
  };

  onContentClicked = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
  };

  listenToScroll = (e: Event) => {
    const winScroll = e.target as HTMLDivElement;
    this.setState({
      scrollPosition: winScroll.scrollTop,
    });
  };

  download = (meta?: FileMetaData) => {
    if (this.props.type !== undefined && meta !== undefined) {
      ImageService.downloadImage(meta.imageId, meta.imageType ?? this.props.type);
    } else if (meta?.data !== undefined) {
      const downloadLink = document.createElement("a");
      const normalizedContentType = FileHelper.normalizeContentType(meta.fileName, meta.contentType);
      downloadLink.href = `data:${normalizedContentType};base64,${meta.data}`;
      downloadLink.download = `${meta.fileName ?? meta.imageId?.toString()}`;
      downloadLink.click();
    }
  };

  transformComponentsRender = ({ zoomIn, zoomOut, resetTransform, ...rest }: ReactZoomPanPinchHandlers) => (
    <TransformComponent
      wrapperClass={clsx(
        cssExports.fileViewerWrapper,
        this.props.currentFile?.contentType === "html" ? cssExports.htmlWrapper : ""
      )}
      contentClass={cssExports.fileViewerZoomContainer}
    >
      <Grid container alignContent={!this.props.fullScreen ? "center" : undefined}>
        <FileViewerInner
          key={this.props.parentId}
          isLoading={this.props.isLoading}
          currentFile={this.props.currentFile ?? ({} as FileMetaData)}
          currentPage={this.props.currentPage}
          containerHeight={
            this.props.fullScreen
              ? this.state.containerHeight
              : this.context?.containerDimensions?.height ?? this.state.containerHeight
          }
          containerWidth={
            this.props.fullScreen
              ? this.state.containerWidth
              : this.context?.containerDimensions?.width ?? this.state.containerWidth
          }
          fullScreen={this.props.fullScreen}
          updateMetaInfo={this.props.updateMetaInfo}
        />
        {this.imageActionContainerRef ? (
          ReactDOM.createPortal(
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                <RoundButton size="xs" onClick={this.onZoomOut.bind(this, zoomOut)}>
                  -
                </RoundButton>
              </Grid>
              <Grid item>
                <InputField
                  label=" "
                  inputBaseProps={{
                    value: this.state.zoom,
                    readOnly: true,
                  }}
                  isNumericString
                  allowNegative={false}
                  maxNumber={360}
                  suffix={"%"}
                // onChangeCustom={this.onZoomInputChanged.bind(this, zoomIn, zoomOut)}
                />
              </Grid>
              <Grid item>
                <RoundButton size="xs" onClick={this.onZoomIn.bind(this, zoomIn)}>
                  +
                </RoundButton>
              </Grid>
            </Grid>,
            this.imageActionContainerRef
          )
        ) : (
          <></>
        )}
      </Grid>
    </TransformComponent>
  );

  render() {
    return (
      <>
        <Grid
          className={clsx(
            cssExports.fileViewer,
            this.state.scrollPosition > 20 ? cssExports.scrolling : "",
            this.props.className
          )}
          item
          container
          md={this.props.fullScreen ? 6 : 12}
          xs={this.props.fullScreen ? 10 : 12}
          onClick={this.onContentClicked}
          style={{
            width:
              !this.props.fullScreen && this.context?.containerDimensions
                ? `${this.context.containerDimensions.width}px`
                : undefined,
            height:
              !this.props.fullScreen && this.context?.containerDimensions
                ? `${this.context.containerDimensions.height}px`
                : undefined,
          }}
        >
          <Grid
            item
            container
            justifyContent="center"
            alignItems="center"
            className={clsx(cssExports.fileViewerContainerOuter, this.props.fullScreen ? "" : cssExports.thumbnail)}
            ref={(component: any) => {
              if (!this.frameRefMounted && component && (component.clientHeight > 0 || component.clientWidth > 0)) {
                this.frameRef = component;
                this.frameRefMounted = true;
                this.setFrameWidthHeight();
              }
            }}
            onClick={this.props.onClick}
          >
            <Grid
              ref={(component: any) => (this.containerRef = component)}
              style={{
                position: this.props.fullScreen ? "initial" : "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                width: "100%",
                height: "100%",
                overflow:
                  this.props.currentFile?.contentType?.toLowerCase() === FileEnums.ContentTypes.ApplicationPDF
                    ? undefined
                    : this.props.fullScreen
                      ? "visible"
                      : "hidden",
              }}
            >
              {this.props.isLoading ? (
                <LoadingWithLogo />
              ) : (
                <TransformWrapper
                  onZoom={this.onZoom}
                  wheel={{
                    disabled: true,
                  }}
                >
                  {this.transformComponentsRender}
                </TransformWrapper>
              )}
            </Grid>

            {this.props.imageCount ? (
              <Grid
                item
                container
                justifyContent="center"
                alignItems="center"
                className={clsx(cssExports.pagination, !this.props.fullScreen ? cssExports.showBackground : "")}
                direction="row"
                spacing={2}
                onClick={this.onContentClicked}
              >
                <EntityPagination
                  total={this.props.imageCount}
                  onCurrentIndexUpdate={this.onCurrentIndexUpdate}
                  navigationText={
                    this.props.fullScreen
                      ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Image)
                      : ""
                  }
                  initialIndex={this.initialIndex}
                  numPerPage={this.props.imageCount}
                  preserveIndexOnPageNumberChange
                />
                {this.props.fullScreen ? (
                  <>
                    <Grid item>
                      <Divider orientation="vertical" flexItem className={cssExports.divider} />
                    </Grid>
                    <Grid
                      item
                      container
                      className={cssExports.zoomContainer}
                      ref={(component: any) => (this.imageActionContainerRef = component)}
                      direction="row"
                    ></Grid>
                  </>
                ) : (
                  <></>
                )}
                {this.props.onDeleteClick && !(this.props.currentFile?.locked) ? (
                  <>
                    <Grid item>
                      <Divider orientation="vertical" flexItem className={cssExports.divider} />
                    </Grid>
                    <Grid item>
                      <RoundButton size="xs" onClick={this.props.onDeleteClick.bind(null, this.props.currentPage - 1)}>
                        <Icon icon={IconDefinitionConstants.WTrashAlt} />
                      </RoundButton>
                    </Grid>
                  </>
                ) : (
                  <></>
                )}
                {this.props.preventDownload ? (
                  <></>
                ) : (
                  <>
                    <Grid item>
                      <Divider orientation="vertical" flexItem className={cssExports.divider} />
                    </Grid>
                    <Grid item>
                      <RoundButton
                        size="xs"
                        onClick={
                          this.props.onDownloadClick
                            ? this.props.onDownloadClick.bind(null, this.props.currentPage - 1)
                            : this.download.bind(null, this.props.currentFile)
                        }
                      >
                        <Icon icon={IconDefinitionConstants.WDownload} />
                      </RoundButton>
                    </Grid>
                  </>
                )}
              </Grid>
            ) : (
              <></>
            )}
          </Grid>
        </Grid>
      </>
    );
  }
}

export default FileViewer;
